import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Analytics, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { analytics } from '../firebase/firebase'; // your existing analytics export
import { trackEvent } from '../firebase/analytics';

type Consent = {
  analytics: boolean; // only category we expose (non-essential)
};

type ConsentContextValue = {
  consent: Consent;
  hasChoice: boolean; // has the user made a choice yet?
  acceptAll: () => void;
  rejectAll: () => void;
  setAnalytics: (enabled: boolean) => void;
  openManager: () => void;
  closeManager: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

const LS_KEY = 'gc:consent:v1';

function readStored(): { consent: Consent; hasChoice: boolean } {
  trackEvent({ name: 'read_consent' });
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { consent: { analytics: false }, hasChoice: false };
    const parsed = JSON.parse(raw);
    return {
      consent: { analytics: Boolean(parsed?.consent?.analytics) },
      hasChoice: Boolean(parsed?.hasChoice),
    };
  } catch {
    return { consent: { analytics: false }, hasChoice: false };
  }
}

function writeStored(consent: Consent, hasChoice: boolean) {
  trackEvent({ name: 'write_consent', params: { consent, hasChoice } });
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ consent, hasChoice }));
  } catch {
    /* ignore */
  }
}

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<Consent>(() => readStored().consent);
  const [hasChoice, setHasChoice] = useState<boolean>(() => readStored().hasChoice);
  const [showBanner, setShowBanner] = useState<boolean>(() => !readStored().hasChoice);
  const [showManager, setShowManager] = useState<boolean>(false);

  // Keep Firebase Analytics gated by consent
  useEffect(() => {
    if (analytics) {
      // Off by default until user accepts; enable when analytics=true
      setAnalyticsCollectionEnabled(analytics as Analytics, consent.analytics);
      trackEvent({ name: 'consent_updated', params: { analytics: consent.analytics } });
    }
  }, [consent.analytics]);

  const acceptAll = () => {
    const next: Consent = { analytics: true };
    setConsent(next);
    setHasChoice(true);
    setShowBanner(false);
    writeStored(next, true);
  };

  const rejectAll = () => {
    const next: Consent = { analytics: false };
    setConsent(next);
    setHasChoice(true);
    setShowBanner(false);
    writeStored(next, true);
  };

  const setAnalytics = (enabled: boolean) => {
    const next: Consent = { analytics: enabled };
    setConsent(next);
    setHasChoice(true);
    writeStored(next, true);
  };

  const openManager = () => setShowManager(true);
  const closeManager = () => setShowManager(false);

  const value = useMemo<ConsentContextValue>(() => ({
    consent,
    hasChoice,
    acceptAll,
    rejectAll,
    setAnalytics,
    openManager,
    closeManager,
  }), [consent, hasChoice]);

  return (
    <ConsentContext.Provider value={value}>
      {children}

      {/* Banner (first visit) */}
      {showBanner && (
        <div className="fixed inset-x-0 bottom-0 z-[9998] p-3">
          <div className="mx-auto max-w-4xl rounded-2xl bg-white/90 backdrop-blur border shadow-lg p-4">
            <h3 className="font-semibold text-slate-800">Cookies on Great Child</h3>
            <p className="text-sm text-slate-600 mt-1">
              We use essential cookies to make our site work and optional analytics (Firebase) to understand how it’s used.
              We’ll only enable analytics if you accept.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                className="px-4 py-2 rounded-lg text-white bg-slate-800 hover:bg-slate-900"
                onClick={acceptAll}
              >
                Accept all
              </button>
              <button
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={rejectAll}
              >
                Reject all
              </button>
              <button
                className="ml-auto px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={() => setShowManager(true)}
              >
                Manage settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {showManager && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur"
          aria-modal="true"
          role="dialog"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowManager(false); }}
        >
          <div className="bg-white w-[95vw] max-w-xl rounded-2xl shadow-xl p-5">
            <h3 className="text-lg font-semibold text-slate-800">Cookie preferences</h3>
            <p className="text-sm text-slate-600 mt-1">
              Choose which cookies we use. Your choice is stored in your browser and you can change it at any time.
            </p>

            <div className="mt-4 space-y-3">
              <div className="rounded-lg border p-3">
                <div className="font-medium text-slate-800">Necessary</div>
                <div className="text-sm text-slate-600">
                  Required for basic site functionality (security, network, form submission). Always on.
                </div>
              </div>

              <div className="rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-800">Analytics (Firebase)</div>
                    <div className="text-sm text-slate-600">
                      Helps us understand page visits and improve the site. Optional.
                    </div>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={consent.analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                    />
                    <span className="px-3 py-1 rounded-full border bg-white peer-checked:bg-lime-500 peer-checked:text-white">
                      {consent.analytics ? 'On' : 'Off'}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-5 flex gap-2 justify-end">
              <button
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={() => setShowManager(false)}
              >
                Close
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white bg-slate-800 hover:bg-slate-900"
                onClick={() => { writeStored(consent, true); setShowManager(false); }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </ConsentContext.Provider>
  );
};

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within <ConsentProvider>');
  return ctx;
}
